import React from 'react';

import { GUIDE_TEXT_MAX_WIDTH } from '@backrs_ui/grow-ui/src/components/GuidePage';
import type {
  StudyHabit,
  StudyHabitBlock,
} from '@backrs_ui/grow-ui/src/pages/StudyHabitsGuide/content/types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type HabitSectionProps = Readonly<{
  content: StudyHabit;
}>;

const HabitBlock: React.FC<{ block: StudyHabitBlock }> = ({ block }) => {
  if (block.callout) {
    return (
      <Box sx={calloutSx}>
        <Typography variant="text-sm-semibold" sx={calloutLabelSx}>
          {block.callout.label}
        </Typography>
        <Typography variant="text-sm-regular" sx={calloutTextSx}>
          {block.callout.text}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={blockSx}>
      {block.heading && (
        <Typography variant="text-md-semibold" sx={headingSx}>
          {block.heading}
        </Typography>
      )}
      {block.text && (
        <Typography variant="text-sm-regular" sx={bodySx}>
          {block.text}
        </Typography>
      )}
      {block.bullets && (
        <Box component={block.ordered ? 'ol' : 'ul'} sx={listSx}>
          {block.bullets.map((bullet) => (
            <Typography key={bullet} component="li" variant="text-sm-regular" sx={bulletSx}>
              {bullet}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const HabitSection: React.FC<HabitSectionProps> = ({ content }) => (
  <Box sx={containerSx}>
    {content.subtitle && (
      <Typography variant="text-md-semibold" sx={subtitleSx}>
        {content.subtitle}
      </Typography>
    )}
    {content.intro && (
      <Typography variant="text-md-regular" sx={introSx}>
        {content.intro}
      </Typography>
    )}
    {content.blocks.map((block, index) => (
      <HabitBlock key={block.heading ?? block.callout?.label ?? index} block={block} />
    ))}
  </Box>
);

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const introSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const blockSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const headingSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  m: 0,
  pl: backrs.spacing['spacing-2xl'],
});

const bulletSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  '&::marker': {
    color: backrs.colors['Colors/Text/text-quaternary'],
  },
});

const calloutSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  p: backrs.spacing['spacing-xl'],
  borderTopRightRadius: backrs.radius['radius-lg'],
  borderBottomRightRadius: backrs.radius['radius-lg'],
  borderLeft: `3px solid ${backrs.colors['Colors/Border/border-brand']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const calloutLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-primary'],
});

const calloutTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});
